import { themeAssetExists, themeAssetUrl } from './themeAssets';

/**
 * Cinematic background themes — the single source of truth.
 *
 * Each theme is just `{ id, name, file }` where `file` is the `tN.webp` number in
 * `src/assets/themes/` (or `null` for the clean gradient default, which has no
 * image). That one file drives ALL three CSS values for the theme:
 *  - `--skin-bg`  → page background (readability overlay + `url(tN.webp)`)
 *  - `--hero-bg`  → the raw `url(tN.webp)` clipped into the WINR wordmark
 *  - the wheel thumbnail → the same `tN.webp`, scaled down by CSS (no thumb file)
 *
 * No Unsplash URLs, no inline gradients masquerading as photos. If a `tN.webp`
 * isn't in the repo, that theme reports as missing (see `missingThemes`) instead
 * of silently falling back to a fake.
 *
 * The `tN` mapping is authoritative — it's the only thing that says which file is
 * which theme. Keep it accurate.
 */
export interface Theme {
  id: string;
  name: string;
  /** `tN.webp` number, or null for the imageless gradient default. */
  file: number | null;
}

export const THEMES: Theme[] = [
  { id: 'framer', name: 'Default', file: null },
  { id: 'abyss', name: 'Abyss', file: 1 },
  { id: 'aurora', name: 'Aurora', file: 2 },
  { id: 'cipher', name: 'Cipher', file: 3 },
  { id: 'orbit', name: 'Orbit', file: 4 },
  { id: 'dynasty', name: 'Dynasty', file: 5 },
  { id: 'black', name: 'Black', file: 6 },
  // t7–t12 — newly added assets. The id is persisted as `profiles.skin_id`, so
  // these are stable identifiers; rename the display `name` freely to match each
  // image, but don't change an `id` once people have selected it.
  { id: 'ember', name: 'Ember', file: 7 },
  { id: 'nebula', name: 'Nebula', file: 8 },
  { id: 'mirage', name: 'Mirage', file: 9 },
  { id: 'vortex', name: 'Vortex', file: 10 },
  { id: 'onyx', name: 'Onyx', file: 11 },
  { id: 'solstice', name: 'Solstice', file: 12 },
];

const DEFAULT_THEME = THEMES[0]!;

/** Readability scrim layered over the photo so foreground UI stays legible. */
const OVERLAY = 'linear-gradient(rgba(4,4,6,0.62), rgba(4,4,6,0.78))';

/** Wordmark fill (electric blue) when no image theme is active. */
export const DEFAULT_HERO = 'linear-gradient(135deg, #8fd2ff 0%, #54b9fe 50%, #1e6fb0 100%)';

/** Default-tile thumbnail (the imageless off-charcoal gradient). */
export const DEFAULT_THUMB = 'linear-gradient(160deg,#1c1c20,#0e0e11)';

const STORAGE = 'winr-skin';
const CUSTOM = 'winr-skin-custom';

export function getTheme(id: string): Theme | undefined {
  return THEMES.find((t) => t.id === id);
}

/** Themes whose `tN.webp` file is declared but not present in the repo. */
export function missingThemes(): Theme[] {
  return THEMES.filter((t) => t.file != null && !themeAssetExists(t.file));
}

/** The `--skin-bg` / `--hero-bg` pair for a theme (or the custom upload). */
function cssVarsFor(id: string): { skinBg: string; heroBg: string } {
  if (id === 'custom') {
    const data = localStorage.getItem(CUSTOM);
    return data
      ? { skinBg: `${OVERLAY}, url(${data})`, heroBg: `url(${data})` }
      : { skinBg: 'none', heroBg: DEFAULT_HERO };
  }
  const theme = getTheme(id) ?? DEFAULT_THEME;
  const url = themeAssetUrl(theme.file);
  // No image (default) OR the file is missing → clean gradient, never a fake photo.
  if (!url) return { skinBg: 'none', heroBg: DEFAULT_HERO };
  return { skinBg: `${OVERLAY}, url(${url})`, heroBg: `url(${url})` };
}

/** Url the browser should eagerly fetch/preload for the active theme (or null). */
export function activeThemeImageUrl(id: string): string | null {
  if (id === 'custom') return localStorage.getItem(CUSTOM);
  return themeAssetUrl(getTheme(id)?.file);
}

/** Apply a theme's background + wordmark fill + `data-skin` (no persistence). */
export function applyTheme(id: string): void {
  const root = document.documentElement;
  const { skinBg, heroBg } = cssVarsFor(id);
  root.style.setProperty('--skin-bg', skinBg);
  root.style.setProperty('--hero-bg', heroBg);
  root.setAttribute('data-skin', id);
}

/** Apply + persist a theme selection to localStorage (works logged-in or out). */
export function setTheme(id: string): void {
  applyTheme(id);
  localStorage.setItem(STORAGE, id);
}

export function setCustomTheme(dataUrl: string): void {
  localStorage.setItem(CUSTOM, dataUrl);
  setTheme('custom');
}

export function currentThemeId(): string {
  if (typeof window === 'undefined') return DEFAULT_THEME.id;
  return localStorage.getItem(STORAGE) || DEFAULT_THEME.id;
}

export function themeName(id: string): string {
  if (id === 'custom') return 'Custom';
  return getTheme(id)?.name ?? DEFAULT_THEME.name;
}
